/**
 * The table themes (THM, brought forward from Phase 5 at the owner's request,
 * 2026-09-25).
 *
 * A theme is nothing but a swapped token set (`styles/tokens.css`, CLAUDE.md §6): every
 * colour lives there, under `[data-theme='<id>']`. This file only names them and says
 * which order to offer them in. It holds no colours, so the Settings swatches are drawn
 * by giving each one its theme attribute and letting the tokens do the rest.
 *
 * `tier` is here so some themes can become paid later. It is data only. ⚠️ Whether a
 * player OWNS a premium theme is an entitlement, and entitlements are decided on the
 * server from verified purchases (PRD §7, `owner-only`; Phase 5). A locked button in
 * the client is UX, not access control (CLAUDE.md §10).
 *
 * Owner, 2026-09-27 (D-047): Pool hall and Tournament blue are the free themes; Black &
 * gold and Casino red join the supporter pack. Until the pack is released
 * (`RELEASES.supporterPack`), only the free themes are offered at all.
 */
import { RELEASES } from './releases.ts';

export type ThemeTier = 'free' | 'premium';

export interface ThemeInfo {
  readonly id: string;
  readonly name: string;
  readonly tier: ThemeTier;
}

export const THEMES = [
  { id: 'pool-hall', name: 'Pool hall', tier: 'free' },
  { id: 'blue-felt', name: 'Tournament blue', tier: 'free' },
  { id: 'black-felt', name: 'Black & gold', tier: 'premium' },
  { id: 'red-felt', name: 'Casino red', tier: 'premium' },
  { id: 'grey-felt', name: 'Tournament grey', tier: 'premium' },
  { id: 'parlour', name: 'Vintage parlour', tier: 'premium' },
  { id: 'purple-night', name: 'Purple night', tier: 'premium' },
  { id: 'burnt-orange', name: 'Burnt orange', tier: 'premium' },
] as const satisfies readonly ThemeInfo[];

export type ThemeId = (typeof THEMES)[number]['id'];

export const DEFAULT_THEME: ThemeId = 'pool-hall';

export const THEME_IDS: readonly ThemeId[] = THEMES.map((theme) => theme.id);

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

/** The themes a player is offered: all of them once the pack is out, else the free ones. */
export function offeredThemes(packReleased: boolean): readonly ThemeInfo[] {
  return packReleased ? THEMES : THEMES.filter((theme) => theme.tier === 'free');
}

export const OFFERED_THEMES: readonly ThemeInfo[] = offeredThemes(RELEASES.supporterPack);

/** A theme id that is offered right now. A saved theme that is not falls back to default. */
export function isOfferedTheme(
  value: unknown,
  offered: readonly ThemeInfo[] = OFFERED_THEMES,
): value is ThemeId {
  return isThemeId(value) && offered.some((theme) => theme.id === value);
}
